import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import {
  getPrevuraDisplayItemCount,
  hasPrevuraIncludedGift,
  hasPrevuraPackage,
  isPrevuraPackageHandle,
  isPrevuraPillowHandle,
} from '@theme/prevura-cart-contract';

const GIFT_CELEBRATION_KEY = 'prevura-gift-celebrated';
const GIFT_VERIFY_DELAYS = [500, 1400, 2600];

let giftVerificationRun = 0;

function readCelebratedGift() {
  try {
    return sessionStorage.getItem(GIFT_CELEBRATION_KEY) || '';
  } catch (_) {
    return '';
  }
}

function rememberCelebratedGift(signature) {
  try {
    if (signature) sessionStorage.setItem(GIFT_CELEBRATION_KEY, signature);
    else sessionStorage.removeItem(GIFT_CELEBRATION_KEY);
  } catch (_) {
    // Storage can be unavailable in privacy-restricted storefront contexts.
  }
}

function includedGiftSignature(cart) {
  const packageItem = (cart?.items || []).find(
    (item) => isPrevuraPackageHandle(item.handle || item.product_handle || '')
  );
  const packageProductId = String(packageItem?.product_id || '');
  const giftItem = (cart?.items || []).find((item) => {
    const properties = item.properties || {};
    const handle = item.handle || item.product_handle;
    return (
      String(properties._auto_added_gift || '').toLowerCase() === 'true' &&
      String(properties._parent_product_ids || '').includes(packageProductId) &&
      isPrevuraPillowHandle(handle) &&
      item.final_line_price === 0
    );
  });

  return giftItem?.key || (giftItem?.variant_id ? String(giftItem.variant_id) : '');
}

function showGiftCelebration() {
  document.querySelector('.pv-gift-celebration')?.remove();

  const celebration = document.createElement('div');
  celebration.className = 'pv-gift-celebration';
  celebration.setAttribute('role', 'status');
  celebration.setAttribute('aria-live', 'polite');
  celebration.innerHTML = `
    <span class="pv-gift-celebration__burst" aria-hidden="true">
      <span></span><span></span><span></span><span></span><span></span><span></span><span></span><span></span>
      <span></span><span></span><span></span><span></span>
    </span>
    <span class="pv-gift-celebration__ring" aria-hidden="true"></span>
    <span class="pv-gift-celebration__panel">
      <span class="pv-gift-celebration__mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M12 3.75v16.5M3.75 12h16.5M6.17 6.17l11.66 11.66M17.83 6.17 6.17 17.83" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="pv-gift-celebration__copy">
        <span class="pv-gift-celebration__eyebrow">Paket vollständig</span>
        <strong>ERGO PILLOW ohne Aufpreis</strong>
        <small>Das ERGO PILLOW ist ohne Aufpreis im Präventionspaket enthalten.</small>
      </span>
    </span>
  `;

  // An open native <dialog> lives in the browser's top layer. Mount the
  // confirmation inside it so the message cannot render invisibly behind the
  // cart even with a high z-index; fall back to the page for non-drawer adds.
  const celebrationHost = document.querySelector('.cart-drawer__dialog[open]') || document.body;
  celebrationHost.append(celebration);
  requestAnimationFrame(() => celebration.classList.add('is-visible'));

  window.setTimeout(() => {
    celebration.classList.remove('is-visible');
    window.setTimeout(() => celebration.remove(), 360);
  }, 4600);
}

function synchronizeGiftCelebration(cart) {
  const hasGift = hasPrevuraIncludedGift(cart);
  const giftSignature = includedGiftSignature(cart);

  if (!hasGift) {
    rememberCelebratedGift('');
  } else if (giftSignature && readCelebratedGift() !== giftSignature) {
    rememberCelebratedGift(giftSignature);
    // The cart drawer already confirms the included pillow; no extra celebration popup.
  }

  return hasGift;
}

function cartJsonUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.replace(/\/?$/, '/')}cart.js`;
}

/**
 * Publish one normalized final cart snapshot. GiftTrigger's free pillow is a
 * second Shopify line, but PREVURA presents the package as one purchase.
 * @param {object} cart
 */
function publishGiftCart(cart) {
  const displayItemCount = getPrevuraDisplayItemCount(cart);
  const displayCart = { ...cart, item_count: displayItemCount };

  document.dispatchEvent(
    new CartUpdateEvent(displayCart, 'gift-trigger', {
      source: 'gift-trigger',
      itemCount: displayItemCount,
    })
  );
}

/**
 * GiftTrigger normally publishes its public event after inserting the gift.
 * This bounded fallback covers a delayed extension response without polling
 * or coupling the theme to the extension's private DOM.
 */
function verifyGiftAfterCartAdd() {
  const run = ++giftVerificationRun;

  for (const delay of GIFT_VERIFY_DELAYS) {
    window.setTimeout(async () => {
      if (run !== giftVerificationRun) return;

      try {
        const response = await fetch(cartJsonUrl(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const cart = await response.json();
        if (!synchronizeGiftCelebration(cart)) return;

        giftVerificationRun += 1;
        publishGiftCart(cart);
      } catch (_) {
        // The native cart remains functional if this optional verification fails.
      }
    }, delay);
  }
}

async function initializeGiftCelebration() {
  try {
    const response = await fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const cart = await response.json();
    const signature = includedGiftSignature(cart);
    rememberCelebratedGift(hasPrevuraIncludedGift(cart) ? signature : '');
  } catch (_) {
    // A failed optional initialization must not affect the native cart.
  }
}

const giftCelebrationReady = initializeGiftCelebration();

/**
 * Normalize GiftTrigger's final cart snapshot for the PREVURA drawer. The
 * extension remains responsible for its visible add-to-cart confirmation.
 */
document.addEventListener('gift-trigger:cart-updated', async (event) => {
  await giftCelebrationReady;

  const cart = event.detail?.cart;

  if (!cart || typeof cart.item_count !== 'number') return;

  synchronizeGiftCelebration(cart);
  giftVerificationRun += 1;
  publishGiftCart(cart);
});

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  await giftCelebrationReady;

  if (event.detail?.sourceId === 'gift-trigger' || event.detail?.data?.source === 'gift-trigger') return;

  const cart = event.detail?.resource;
  if (!cart || !Array.isArray(cart.items)) return;

  const hasGift = synchronizeGiftCelebration(cart);
  if (hasGift || !hasPrevuraPackage(cart)) {
    giftVerificationRun += 1;
    return;
  }

  verifyGiftAfterCartAdd();
});

